import { getRefreshToken, setRefreshToken } from "../../session/TokenStore";
export async function getAccessToken() {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;
    try {
        const response = await fetch(`${baseUrl}/token/refresh/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh: refreshToken })
        });
        if (!response.ok) {
            console.error("Failed to refresh access token, status:", response.status);
            return null;
        }
        const data = await response.json();
        const accessToken = data.access;
        return accessToken || null;
    } catch (e) {
        console.error("Error refreshing access token:", e);
        return null;
    }
}
export async function getSurveys() {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const token = await getAccessToken();
    const header = token ? { "Authorization": `Bearer ${token}` } : {};
    const res = await fetch(`${baseUrl}/surveys/`, {
        method: "GET",
        headers: header
    });

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to fetch surveys";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }

    return data;
}

function tokenFromQuery() {
    try {
        const p = new URLSearchParams(window.location.search);
        return p.get("token");
    } catch {
        return null;
    }
}

export async function getSurvey(id) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const queryToken = tokenFromQuery();
    const url = queryToken ? `${baseUrl}/surveys/${id}/?token=${encodeURIComponent(queryToken)}` : `${baseUrl}/surveys/${id}/`;

    // If a query token is present we don't need Authorization header.
    // Otherwise, try to refresh an access token for authenticated requests.
    let headers = {};
    if (!queryToken) {
        const bearer = await getAccessToken();
        if (bearer) headers = { Authorization: `Bearer ${bearer}` };
    }

    const res = await fetch(url, {
        method: "GET",
        headers,
    });

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to fetch survey";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }

    return data;
}

export async function getSessionToken(surveyID) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

    const res = await fetch(`${baseUrl}/surveys/sessions/start/`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ "survey": surveyID, lang: "en" })
        }
    );

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to start survey session";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data.sessionToken;
}

export async function postSurveyResponses(body, participantID) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const token = await getSessionToken(body.surveyId);
    body.sessionToken = token;
    const res = await fetch(`${baseUrl}/surveys/responses/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });
    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to submit survey answers";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }

    return data;
}

export async function registerUser(email, password) {
    const baseUrl = process.env.REACT_APP_API_URL;
    const res = await fetch(`${baseUrl}/auth/register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            "username": email,
            "email": email,
            "password": password
        })
    });

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to register user";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }
}

// Register with explicit username; if email is not provided, use a fallback synthetic email.
export async function registerUserWithUsername(username, password, email) {
    const baseUrl = process.env.REACT_APP_API_URL;
    const body = {
        username,
        email: email || `${username}@rid.local`,
        password,
    };
    const res = await fetch(`${baseUrl}/auth/register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to register user";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data;
}

export async function loginUser(username, password) {
    const baseUrl = process.env.REACT_APP_API_URL;

    const res = await fetch(`${baseUrl}/token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            "username": username,
            "password": password

        })
    });

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to log in user";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    try {
        if (data?.refresh) setRefreshToken(data.refresh);
    } catch { }
    return data;
}

// Try to log in with rid as username/password; if it fails due to lack of account, register then log in.
export async function ensureLoginWithRid(rid) {
    if (!rid) return null;
    try {
        const login = await loginUser(rid, rid);
        return login;
    } catch (e) {
        // Attempt to register then login
        try {
            await registerUserWithUsername(rid, rid);
        } catch (regErr) {
            // If registration fails because user exists, continue to login
            if ((regErr?.status || 0) !== 400 && (regErr?.status || 0) !== 409) {
                throw regErr;
            }
        }
        const login2 = await loginUser(rid, rid);
        return login2;
    }
}


export async function createEmptySurvey() {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const err = new Error("Not authenticated");
        err.status = 401;
        throw err;
    }
    const res = await fetch(`${baseUrl}/surveys/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}` },
        body: JSON.stringify({ title: { en: "" } })
    });
    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        const message = (data && data.detail) || res.statusText || "Failed to create survey";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data;
}

export async function checkTicketNumber(ticketNumber) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

    return true;
}

export async function downloadSurveyResponse(responseID) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const answers = await fetch(`${baseUrl}/surveys/responses/${responseID}/answers/`, {
        method: "GET",
        headers: { "Content-Type": "application/json" }
    });
    let data;
    try {
        data = await answers.json();
    } catch {
        data = null;
    }
    if (!answers.ok) {
        const message = (data && data.detail) || answers.statusText || "Failed to download survey answers";
        const err = new Error(message);
        err.status = answers.status;
        err.data = data;
        throw err;
    }
    return data;
}
